import Jumbotron from '@/components/core/Jumbotron/Jumbotron';
import Section from '@/components/core/Section/Section';
import StoriesPosts from '@/components/features/posts/StoriesPosts/StoriesPosts';
import { Layout } from '@/components/shared/layouts/Layout/Layout';
import { linkType } from '@/components/shared/navigations/BottomNavigation/BottomNavigation';
import { ColorTypeOptions } from '@/components/shared/navigations/CoreNavigation/CoreNavigation';
import SharePanel from '@/components/shared/share-panel/SharePanel';
import { useClient } from '@/components/shared/utilities/AppProvider/AppProvider';
import { IStoryResource } from '@/interfaces/IStoryResource';
import { createClient } from '@/jsonapi/createClient';
import { getSafeLocale } from '@/utils/locale';
import { serializeCollectionResource } from '@/utils/serilize-resource';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import classNames from 'classnames';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';

import styles from '../../styles/share.module.scss';

type ShareProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const SharePage: NextPage<ShareProps> = ({ storiesInitialData }) => {
	const { t } = useTranslation();
	const { query } = useRouter();
	const client = useClient();
	const descriptionClassNames = classNames(styles.captionDescription, styles.shareDescription);

	const {
		data: stories,
		error,
		setSize,
		size,
	} = useSWRInfinite(
		(pageIndex, previousPageData) => {
			if (previousPageData && !previousPageData?.links?.next) return null; // reached the end

			const pageNumber = pageIndex + 1;

			return [
				'story',
				{
					include: 'favoriter',
					page: pageNumber,
					per_page: 16,
					filters: query.filter || 'trending',
				},
			] as const;
		},
		async (key) => {
			const data = await client.findAll(...key);

			return serializeCollectionResource(data);
		},
		{
			fallbackData: [storiesInitialData],
		}
	);

	const { items, isLoadingMore, isReachingEnd, meta } = useMemo(
		() => extractSWRInfiniteData(stories, error, size),
		[stories, error, size]
	);

	const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

	return (
		<>
			<NextSeo title={t('share')} />
			<Layout
				colorTypeActive={ColorTypeOptions.SECONDARY}
				bottomNavigation={{ leftLink: linkType.VISIT, rightLink: linkType.COLLECTION }}
			>
				<Jumbotron
					color={Jumbotron.color.SECONDARY}
					maskColor={Jumbotron.maskColor.DIMMED}
					backgroundImage="/images/share-header.jpg"
					contentClassName={styles.jumbotron}
				>
					<div className={styles.caption}>
						<div className={styles.captionLeft}>
							<h1 className={styles.captionTitle}>{t('shared-breakups')}</h1>
							<p className={descriptionClassNames}>{t('share-a-breakup-story-lock-i')}</p>
						</div>
					</div>
				</Jumbotron>
				<Section color="Dimmed">
					<StoriesPosts
						posts={items}
						meta={meta}
						onLoadMore={onLoadMore}
						hasMore={!isReachingEnd}
						loading={isLoadingMore}
					/>
					<SharePanel />
				</Section>
			</Layout>
		</>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ locale, query, req }) => {
	const client = createClient({ locale, cookie: req.headers.cookie });

	const storiesData = await client.findAll<IStoryResource>('story', {
		include: 'favoriter',
		page: 1,
		per_page: 16,
		filters: query.filter || 'trending',
	});

	const storiesInitialData = serializeCollectionResource(storiesData);

	return {
		props: {
			storiesInitialData,
			...(await serverSideTranslations(getSafeLocale(locale), ['common'])),
		},
	};
};

export default SharePage;
